import { useState, useRef, useCallback, useEffect } from 'react';
import { 
  Paperclip, 
  FilePdf, 
  Image as ImageIcon, 
  X, 
  CloudArrowUp,
  Eye,
  Trash,
  Warning,
  Clock,
} from '@phosphor-icons/react';
import { attachmentsApi } from '../../services/api';
import { useOffline } from '../../context/OfflineContext';
import { 
  getAttachmentFromCache, 
  cacheAttachment,
  addPendingAttachment,
  getPendingAttachments,
  removePendingAttachment,
  hasPendingDuplicate,
} from '../../services/attachmentCache';
import AttachmentViewer from './AttachmentViewer';
import LoadingSpinner from './LoadingSpinner';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_ATTACHMENTS = 3;
const ALLOWED_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
];

/**
 * AttachmentUploader - Handles uploading and managing attachments
 * Supports drag & drop, file picker, and displays existing attachments
 * Works offline by storing pending attachments locally
 */
export default function AttachmentUploader({ 
  attachments = [], 
  onAttachmentsChange,
  eventId,
  expenseId,
  disabled = false,
}) {
  const { isOnline } = useOffline();
  const fileInputRef = useRef(null);
  
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Pending attachments (stored locally, awaiting upload)
  const [pendingAttachments, setPendingAttachments] = useState([]);
  
  // Viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerAttachment, setViewerAttachment] = useState(null);
  const [viewerBlobUrl, setViewerBlobUrl] = useState(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState(null);
  
  // Target ID for pending attachments (use eventId or expenseId)
  const targetId = eventId || expenseId;
  const targetType = eventId ? 'event' : 'expense';
  
  // Total count includes both server and pending attachments
  const totalCount = attachments.length + pendingAttachments.length;
  const canUpload = totalCount < MAX_ATTACHMENTS && !disabled;
  
  // Load pending attachments on mount
  useEffect(() => {
    if (targetId) {
      getPendingAttachments(targetId)
        .then(pending => setPendingAttachments(pending))
        .catch(err => console.warn('Failed to load pending attachments:', err));
    }
  }, [targetId]);
  
  // Get icon for file type
  function getFileIcon(fileType) {
    if (fileType === 'application/pdf') {
      return <FilePdf size={24} weight="fill" className="text-red-500" />;
    }
    if (fileType?.startsWith('image/')) {
      return <ImageIcon size={24} weight="fill" className="text-blue-500" />;
    }
    return <Paperclip size={24} />;
  }
  
  // Format file size
  function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  
  // Validate file
  async function validateFile(file) {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'File type not allowed. Allowed: PDF, PNG, JPEG, WebP';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File too large. Maximum size is 5MB';
    }
    // Check for duplicate filename in server attachments
    if (attachments.some(a => a.filename === file.name)) {
      return `A file named "${file.name}" already exists`;
    }
    // Check for duplicate filename in pending attachments
    if (pendingAttachments.some(p => p.filename === file.name)) {
      return `A file named "${file.name}" is already pending upload`;
    }
    return null;
  }
  
  // Handle file upload (online: direct upload, offline: store locally)
  async function handleUpload(file) {
    const validationError = await validateFile(file);
    if (validationError) {
      setUploadError(validationError);
      return;
    }
    
    setUploading(true);
    setUploadError(null);
    
    if (isOnline) {
      // Online: upload directly to server
      try {
        const newAttachment = await attachmentsApi.upload(file, {
          event_id: eventId,
          expense_id: expenseId,
        });
        
        // Update attachments list
        onAttachmentsChange([...attachments, newAttachment]);
        
        // Pre-cache the file for offline access
        try {
          const { blob } = await attachmentsApi.download(newAttachment.id);
          await cacheAttachment(newAttachment.id, blob, newAttachment);
        } catch (cacheErr) {
          console.warn('Failed to cache attachment:', cacheErr);
        }
        
      } catch (err) {
        console.error('Upload failed:', err);
        setUploadError(err.message || 'Upload failed');
      }
    } else {
      // Offline: store locally as pending attachment
      try {
        const pending = await addPendingAttachment(file, targetId, targetType);
        setPendingAttachments(prev => [...prev, pending]);
        console.log(`[AttachmentUploader] Stored pending attachment: ${file.name}`);
      } catch (err) {
        console.error('Failed to store pending attachment:', err);
        setUploadError('Failed to save file locally');
      }
    }
    
    setUploading(false);
  }
  
  // Handle removing a pending attachment
  async function handleRemovePending(tempId) {
    try {
      await removePendingAttachment(tempId);
      setPendingAttachments(prev => prev.filter(p => p.tempId !== tempId));
    } catch (err) {
      console.error('Failed to remove pending attachment:', err);
      setUploadError('Failed to remove file');
    }
  }
  
  // Handle file input change
  function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  }
  
  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (canUpload) setIsDragging(true);
  }, [canUpload]);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (!canUpload) return;
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleUpload(file);
    }
  }, [canUpload]);
  
  // View attachment
  async function handleView(attachment) {
    setViewerAttachment(attachment);
    setViewerOpen(true);
    setViewerLoading(true);
    setViewerError(null);
    setViewerBlobUrl(null);
    
    try {
      // Try to get from cache first
      const cached = await getAttachmentFromCache(attachment.id);
      
      if (cached) {
        setViewerBlobUrl(URL.createObjectURL(cached.blob));
      } else if (isOnline) {
        // Download from server
        const { blob, url } = await attachmentsApi.download(attachment.id);
        setViewerBlobUrl(url);
        
        // Cache for later
        try {
          await cacheAttachment(attachment.id, blob, attachment);
        } catch (cacheErr) {
          console.warn('Failed to cache attachment:', cacheErr);
        }
      } else {
        setViewerError('File not cached. You need to be online to view this file.');
      }
    } catch (err) {
      console.error('Failed to load attachment:', err);
      setViewerError(err.message || 'Failed to load file');
    } finally {
      setViewerLoading(false);
    }
  }
  
  // Close viewer
  function handleCloseViewer() {
    setViewerOpen(false);
    // Revoke blob URL to free memory
    if (viewerBlobUrl) {
      URL.revokeObjectURL(viewerBlobUrl);
      setViewerBlobUrl(null);
    }
    setViewerAttachment(null);
    setViewerError(null);
  }
  
  // Delete attachment
  async function handleDelete(attachmentId) {
    if (!isOnline) {
      setUploadError('You must be online to delete files');
      return;
    }
    
    setDeleting(true);
    
    try {
      await attachmentsApi.delete(attachmentId);
      
      // Update attachments list
      onAttachmentsChange(attachments.filter(a => a.id !== attachmentId));
      
    } catch (err) {
      console.error('Delete failed:', err);
      setUploadError(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  }
  
  return (
    <div className="space-y-3">
      {/* Attachment Viewer Modal */}
      <AttachmentViewer
        isOpen={viewerOpen}
        onClose={handleCloseViewer}
        attachment={viewerAttachment}
        blobUrl={viewerBlobUrl}
        isLoading={viewerLoading}
        error={viewerError}
      />
      
      {/* Existing attachments */}
      {attachments.length > 0 && (
        <div className="space-y-2">
          {attachments.map((attachment) => (
            <div 
              key={attachment.id}
              className="flex items-center gap-3 p-3 rounded-sm"
              style={{ backgroundColor: 'var(--color-bg-surface)' }}
            >
              {getFileIcon(attachment.file_type)}
              
              <div className="flex-1 min-w-0">
                <p 
                  className="text-sm font-medium truncate"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {attachment.filename}
                </p>
                <p 
                  className="text-xs"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  {formatFileSize(attachment.file_size)}
                </p>
              </div>
              
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => handleView(attachment)}
                  className="p-2 rounded-sm transition-colors"
                  style={{ color: 'var(--color-text-secondary)' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--color-bg-raised)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  title="View"
                >
                  <Eye size={18} />
                </button>
                
                {deleteConfirm === attachment.id ? (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleDelete(attachment.id)}
                      disabled={deleting}
                      className="px-2 py-1 text-xs font-medium rounded-sm bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
                    >
                      {deleting ? '...' : 'Delete'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteConfirm(null)}
                      className="px-2 py-1 text-xs font-medium rounded-sm"
                      style={{ 
                        backgroundColor: 'var(--color-bg-raised)',
                        color: 'var(--color-text-secondary)',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(attachment.id)}
                    disabled={!isOnline || disabled}
                    className="p-2 rounded-sm transition-colors disabled:opacity-50"
                    style={{ color: 'var(--color-text-muted)' }}
                    onMouseEnter={(e) => {
                      if (!e.target.disabled) {
                        e.target.style.backgroundColor = 'rgba(193, 96, 92, 0.15)';
                        e.target.style.color = 'var(--color-status-error)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = 'var(--color-text-muted)';
                    }}
                    title={!isOnline ? 'Must be online to delete' : 'Delete'}
                  >
                    <Trash size={18} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Pending attachments (awaiting upload) */}
      {pendingAttachments.length > 0 && (
        <div className="space-y-2">
          {pendingAttachments.map((pending) => (
            <div 
              key={pending.tempId}
              className="flex items-center gap-3 p-3 rounded-sm"
              style={{ 
                backgroundColor: 'var(--color-bg-surface)',
                border: '1px dashed var(--color-border-subtle)',
              }}
            >
              {getFileIcon(pending.fileType)}
              
              <div className="flex-1 min-w-0">
                <p 
                  className="text-sm font-medium truncate"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {pending.filename}
                </p>
                <p 
                  className="text-xs flex items-center gap-1"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  <Clock size={12} />
                  {formatFileSize(pending.fileSize)} • Pending upload
                </p>
              </div>
              
              <button
                type="button"
                onClick={() => handleRemovePending(pending.tempId)}
                disabled={disabled}
                className="p-2 rounded-sm transition-colors disabled:opacity-50 hover:bg-black/5"
                style={{ color: 'var(--color-text-muted)' }}
                title="Remove"
              >
                <Trash size={18} />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Upload area - works both online and offline */}
      {canUpload && (
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => !uploading && !disabled && fileInputRef.current?.click()}
          className={`
            relative border-2 border-dashed rounded-sm p-4 text-center cursor-pointer transition-colors
            ${isDragging ? 'border-solid' : ''}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          style={{
            borderColor: isDragging ? 'var(--color-brand-primary)' : 'var(--color-border-subtle)',
            backgroundColor: isDragging ? 'rgba(93, 144, 133, 0.1)' : 'transparent',
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading || disabled}
          />
          
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <LoadingSpinner size="md" />
              <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                {isOnline ? 'Uploading...' : 'Saving locally...'}
              </span>
            </div>
          ) : (
            <>
              <CloudArrowUp 
                size={32} 
                weight={isDragging ? 'fill' : 'regular'}
                style={{ color: isDragging ? 'var(--color-brand-primary)' : 'var(--color-text-muted)' }}
                className="mx-auto mb-2"
              />
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                {isDragging ? (
                  'Drop file here'
                ) : (
                  <>
                    <span style={{ color: 'var(--color-brand-primary)' }}>Click to upload</span>
                    {' or drag and drop'}
                  </>
                )}
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                PDF, PNG, JPEG, WebP (max 5MB)
                {!isOnline && ' • Will upload when online'}
              </p>
            </>
          )}
        </div>
      )}
      
      {/* Attachment limit message */}
      {totalCount >= MAX_ATTACHMENTS && (
        <p className="text-xs text-center" style={{ color: 'var(--color-text-muted)' }}>
          Maximum {MAX_ATTACHMENTS} attachments reached
        </p>
      )}
      
      {/* Error message */}
      {uploadError && (
        <div 
          className="flex items-center gap-2 p-3 rounded-sm text-sm"
          style={{ 
            backgroundColor: 'rgba(193, 96, 92, 0.1)',
            color: 'var(--color-status-error)',
          }}
        >
          <Warning size={18} weight="fill" />
          <span>{uploadError}</span>
          <button
            type="button"
            onClick={() => setUploadError(null)}
            className="ml-auto p-1 hover:opacity-70"
          >
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
